import { useCallback, useContext, useMemo, useState } from 'react';
import { createHMRProofContext } from '../utils/createHMRProofContext';
import en from './locales/en.json';
import nl from './locales/nl.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import sv from './locales/sv.json';
import es from './locales/es.json';

export type Locale = 'en' | 'nl' | 'de' | 'fr' | 'sv' | 'es';

const LOCALE_STORAGE_KEY = 'locale';

export const SUPPORTED_LOCALES: Locale[] = ['en', 'nl', 'de', 'fr', 'sv', 'es'];

const translations: Record<Locale, Record<string, unknown>> = {
  en,
  nl,
  de,
  fr,
  sv,
  es,
};

function getNestedValue(
  obj: Record<string, unknown>,
  path: string,
): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function detectLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as Locale))
    return stored as Locale;

  for (const lang of navigator.languages ?? [navigator.language]) {
    const code = lang.split('-')[0].toLowerCase() as Locale;
    if (SUPPORTED_LOCALES.includes(code)) return code;
  }
  return 'en';
}

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string>) => string;
};

const I18nContext = createHMRProofContext<I18nContextValue | null>(
  'I18nContext',
  null,
);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      let value = getNestedValue(translations[locale], key);
      if (value == null) {
        // Fallback to English
        value = getNestedValue(translations.en, key);
      }
      if (value == null) return key;

      if (params) {
        for (const [k, v] of Object.entries(params)) {
          value = value.replaceAll(`{${k}}`, v);
        }
      }

      return value;
    },
    [locale],
  );

  const ctx = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={ctx}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
}
